import { useRef, useState } from 'react';
import * as claus from 'src/_shared/services/claus/endpoints';
import { graphql, parse } from 'src/_shared/services/utils.js';
import { readConfigFromAtsIntegration, writeConfigIntoAtsIntegration } from 'src/_shared/services/job-sync/jobSyncConfig';
import {
	STEP_ORDER,
	NOT_FOUND_RE,
	initialSteps,
	listItems,
	buildPromotedConfig,
	assessTargetEntries,
	diffWebhooks,
	findNameCollision,
} from 'src/_shared/services/promote/promote';

/**
 * Orchestrates the four promote steps (CLAUS org → duplicate → webhooks → job-sync config) with
 * per-step status, resume-after-failure, and an adopt-existing path. Retrying calls `run` again:
 * steps already 'done' (or resolved 'manual') are skipped, so the NON-idempotent duplicate can
 * never run twice — once an integration id is known the step is pinned.
 *
 * The config step is the only cross-company ERIN write; whether the backend allows it from the
 * browser is unknown per-environment, and `graphql()` swallows errors into `undefined` — so a
 * failed write points the operator at the seed-config fallback, and `markConfigManual()` lets them
 * resolve the flow as "seeded offline instead".
 */
export function usePromoteFlow() {
	const [phase, setPhase] = useState('idle'); // idle | running | failed | done
	const [steps, setSteps] = useState(initialSteps());
	const [ctx, setCtx] = useState({ orgCreated: false, newIntegrationId: null, configVerified: false });

	const stepsRef = useRef(steps);
	const ctxRef = useRef(ctx);
	const runningRef = useRef(false);

	// The refs are the source of truth for the running orchestration and MUST update synchronously —
	// React may defer state-updater callbacks, and the very next step reads ctxRef/stepsRef in the
	// same tick (e.g. webhooks needs the id the duplicate step just pinned). State mirrors for render.
	function setStep(key, patch) {
		const next = { ...stepsRef.current, [key]: { ...stepsRef.current[key], ...patch } };
		stepsRef.current = next;
		setSteps(next);
	}

	function patchCtx(partial) {
		const next = { ...ctxRef.current, ...partial };
		ctxRef.current = next;
		setCtx(next);
	}

	async function stepEnsureOrg({ targetCompany }) {
		try {
			await claus.getOrg(targetCompany.id);
			return { detail: 'Organization already existed (id == company id)' };
		} catch (err) {
			if (!NOT_FOUND_RE.test(err.message || '')) throw err;
			await claus.createOrg(targetCompany.id, targetCompany.name || 'Organization');
			patchCtx({ orgCreated: true });
			return { detail: 'Organization created (id == company id)' };
		}
	}

	async function stepDuplicate({ sourceOrgId, integration, targetCompany, name, copyCredentials }) {
		if (ctxRef.current.newIntegrationId) {
			return { detail: `Using integration ${ctxRef.current.newIntegrationId}` };
		}
		// Pre-check the name on a fresh list: a same-named integration means a previous promote (or a
		// half-finished one) already created the copy — adopt it instead of minting a second one.
		const existing = await claus.listIntegrations(targetCompany.id).catch(() => null);
		const collision = findNameCollision(existing, name);
		if (collision) {
			throw new Error(
				`An integration named “${name}” already exists on the target (${collision.id}). ` +
					'Rename the copy, or use “Continue against the existing copy” to adopt it.',
			);
		}
		const res = await claus.duplicateIntegration(sourceOrgId, integration.id, {
			targetOrganizationId: targetCompany.id,
			name,
			copyCredentials,
		});
		let newId = res?.id || res?.integration?.id || null;
		if (!newId) {
			// Defensive: the same-org duplicate dialog never reads this response, so its shape is
			// unproven — resolve the id by re-listing rather than guessing.
			const after = await claus.listIntegrations(targetCompany.id).catch(() => null);
			newId = findNameCollision(after, name)?.id || null;
		}
		if (!newId) {
			throw new Error(
				'The duplicate was likely created but its id could not be resolved. Do NOT retry — that would ' +
					'create a second copy. Reopen the dialog and use “Continue against the existing copy”.',
			);
		}
		patchCtx({ newIntegrationId: newId });
		return {
			detail: `Created “${name}” (${newId}) — disabled, ${copyCredentials ? 'credentials copied' : 'WITHOUT credentials'}`,
		};
	}

	async function stepWebhooks({ sourceOrgId, integration, targetCompany }) {
		const srcRes = await claus.listWebhooks(sourceOrgId, integration.id);
		const srcHooks = listItems(srcRes, 'webhooks');
		const tgtRes = await claus.listWebhooks(targetCompany.id, ctxRef.current.newIntegrationId).catch(() => null);
		const tgtHooks = listItems(tgtRes, 'webhooks');
		const { toCreate, skipped } = diffWebhooks(srcHooks, tgtHooks);
		for (const w of toCreate) {
			await claus.createWebhook(targetCompany.id, ctxRef.current.newIntegrationId, w);
		}
		if (srcHooks.length === 0) {
			return { detail: 'Source has no webhooks — register the process-jobs webhook on the copy manually' };
		}
		return { detail: `Copied ${toCreate.length}, skipped ${skipped.length} already present` };
	}

	async function stepConfig({ sourceCompanyId, sourceAtsName, targetCompany, targetAtsName, acks }) {
		const srcRes = await graphql({ input: { id: sourceCompanyId }, query: 'getATSSettingsByCompany' }, 'cognito');
		if (srcRes === undefined) throw new Error('Could not read the source company’s ATS settings.');
		const sourceConfig = readConfigFromAtsIntegration(parse(srcRes?.atsIntegration) || [], sourceAtsName);
		if (!sourceConfig) throw new Error(`No job-sync config found on the source entry “${sourceAtsName}”.`);

		// Fresh target read immediately before writing — atsIntegration saves are last-write-wins on
		// the whole blob, so never write over a stale copy; and re-assess in case it changed.
		const tgtRes = await graphql({ input: { id: targetCompany.id }, query: 'getATSSettingsByCompany' }, 'cognito');
		if (tgtRes === undefined) {
			throw new Error(
				'Could not read the target company’s ATS settings — this backend may not allow cross-company ' +
					'access from the browser. Use the seed-config command below instead (or “Mark as seeded offline”).',
			);
		}
		const freshTargetArr = parse(tgtRes?.atsIntegration) || [];
		const assess = assessTargetEntries(freshTargetArr, targetAtsName);
		if (!assess.chosen) {
			throw new Error(
				`The target company has no ATS entry named “${targetAtsName}” — create the ATS integration there ` +
					'first (Settings → ATS). This flow never auto-creates it (the entry carries credentials and ids).',
			);
		}
		if (assess.overwrite && !acks.overwrite) {
			throw new Error('The target entry already has a job-sync config. Tick the overwrite acknowledgement first.');
		}
		if (assess.shadowed && !acks.shadowed) {
			throw new Error(
				`Another entry (“${assess.winnerAfterWrite}”) already carries a config and wins at runtime ` +
					'(the engine reads the FIRST config-bearing entry). Tick the acknowledgement to proceed anyway.',
			);
		}

		const promoted = buildPromotedConfig(sourceConfig, { targetCompanyId: targetCompany.id, targetAtsName });
		const nextArr = writeConfigIntoAtsIntegration(freshTargetArr, targetAtsName, promoted);
		const saved = await graphql(
			{ input: { companyId: targetCompany.id, atsIntegration: JSON.stringify(nextArr) }, update: 'updateATSIntegration' },
			'cognito',
		);
		if (saved === undefined) {
			throw new Error(
				'The cross-company config write failed — this backend may not allow it from the browser. ' +
					'Use the seed-config command below instead (or “Mark as seeded offline”).',
			);
		}

		// Trust nothing: re-read and confirm the promoted config actually landed, re-pointed and safe.
		const verifyRes = await graphql({ input: { id: targetCompany.id }, query: 'getATSSettingsByCompany' }, 'cognito');
		const landed = readConfigFromAtsIntegration(parse(verifyRes?.atsIntegration) || [], targetAtsName);
		if (!landed || landed.companyId !== targetCompany.id || landed.mode !== 'dry-run') {
			throw new Error('Post-write verification failed — re-read the target company and inspect the config.');
		}
		patchCtx({ configVerified: true });
		return { detail: `Config copied onto “${targetAtsName}” — mode forced to dry-run` };
	}

	const STEP_IMPL = { ensureOrg: stepEnsureOrg, duplicate: stepDuplicate, webhooks: stepWebhooks, config: stepConfig };

	async function run(params) {
		if (runningRef.current) return;
		runningRef.current = true;
		setPhase('running');
		let currentKey = null;
		try {
			for (const key of STEP_ORDER) {
				const status = stepsRef.current[key].status;
				if (status === 'done' || status === 'manual') continue;
				currentKey = key;
				setStep(key, { status: 'running', error: null });
				const outcome = await STEP_IMPL[key](params);
				setStep(key, { status: 'done', detail: outcome.detail || '' });
			}
			setPhase('done');
		} catch (e) {
			if (currentKey) setStep(currentKey, { status: 'failed', error: e.message || String(e) });
			setPhase('failed');
		} finally {
			runningRef.current = false;
		}
	}

	// A previous promote (or an unresolved duplicate) already created the copy — pin its id so the
	// duplicate step is skipped and the remaining steps run against it.
	function adoptExisting(integrationId, detail) {
		patchCtx({ newIntegrationId: integrationId });
		setStep('duplicate', {
			status: 'done',
			error: null,
			detail: detail || `Adopted existing integration ${integrationId}`,
		});
		if (phase === 'failed') setPhase('idle');
	}

	// The operator chose (or was forced onto) the offline seed-config path for the ERIN half.
	function markConfigManual() {
		setStep('config', { status: 'manual', error: null, detail: 'Seeded offline via seed-config.ts' });
		const done = STEP_ORDER.every((k) => {
			const s = stepsRef.current[k].status;
			return s === 'done' || s === 'manual';
		});
		if (done) setPhase('done');
	}

	function reset() {
		runningRef.current = false;
		setPhase('idle');
		const fresh = initialSteps();
		stepsRef.current = fresh;
		setSteps(fresh);
		const freshCtx = { orgCreated: false, newIntegrationId: null, configVerified: false };
		ctxRef.current = freshCtx;
		setCtx(freshCtx);
	}

	return { phase, steps, ctx, run, adoptExisting, markConfigManual, reset };
}
